<template>
  <span class="rbz-button-wrapper" v-bind="$attrs" @keyup.space.stop @keydown.enter.stop>
    <template v-if="link || linkRouteName">
      <template v-if="externalLink">
        <a :href="link" tabindex="-1" target="_blank">
          <form-button-inner-template
            v-bind="{ ...props, disabled: isDisabled }"
            @button-click="emit('button-click', $event)"
            @focus="emit('focus', $event)"
            @blur="emit('blur', $event)"
            v-if="tooltip"
            v-b-tooltip="tooltip"
          />
          <form-button-inner-template
            v-bind="{ ...props, disabled: isDisabled }"
            @button-click="emit('button-click', $event)"
            @focus="emit('focus', $event)"
            @blur="emit('blur', $event)"
            v-else
          />
        </a>
      </template>
      <template v-else>
        <router-link :to="link ? link : linkObject" @click="null" class="router-link" tabindex="-1">
          <form-button-inner-template
            v-bind="{ ...props, disabled: isDisabled }"
            @button-click="emit('button-click', $event)"
            @focus="emit('focus', $event)"
            @blur="emit('blur', $event)"
            v-if="tooltip"
            v-b-tooltip="tooltip"
          />
          <form-button-inner-template
            v-bind="{ ...props, disabled: isDisabled }"
            @button-click="emit('button-click', $event)"
            @focus="emit('focus', $event)"
            @blur="emit('blur', $event)"
            v-else
          />
        </router-link>
      </template>
    </template>
    <template v-else>
      <form-button-inner-template
        v-if="tooltip && lazyTooltip"
        v-bind="{ ...props, disabled: isDisabled }"
        @button-click="emit('button-click', $event)"
        @focus="emit('focus', $event)"
        @blur="emit('blur', $event)"
        @mouseenter="handleTooltipMouseEnter"
        @mouseleave="handleTooltipMouseLeave"
        :id="buttonId"
      />
      <form-button-inner-template
        v-else-if="tooltip && !lazyTooltip"
        v-bind="{ ...props, disabled: isDisabled }"
        @button-click="emit('button-click', $event)"
        @focus="emit('focus', $event)"
        @blur="emit('blur', $event)"
        v-b-tooltip="tooltip"
      />
      <form-button-inner-template
        v-else
        v-bind="{ ...props, disabled: isDisabled }"
        @button-click="emit('button-click', $event)"
        @focus="emit('focus', $event)"
        @blur="emit('blur', $event)"
      />
    </template>
    <Teleport to="body" v-if="props.tooltip && isTooltipActive && props.lazyTooltip">
      <BTooltip :target="buttonId" :content="props.tooltip" triggers="manual" :show="true" />
    </Teleport>
  </span>
</template>

<script setup lang="ts" inheritAttrs="false">
import { computed, ref, onUnmounted } from 'vue';
import { FormButtonVariant, GenericObject } from '../types';
import FormButtonInnerTemplate from './FormButtonInnerTemplate.vue';
import { BTooltip } from 'bootstrap-vue-next';

// Required label or icon
export interface FormButtonProps {
  label?: string;
  icon?: string;
  iconProps?: GenericObject;
  iconPosition?: 'left' | 'right';
  classes?: string;
  tooltip?: string;
  disabled?: boolean;
  isUpdateInProcess?: boolean;
  ignoreUpdateInProcess?: boolean;
  variant?: FormButtonVariant;
  dataQa?: string;
  link?: string;
  externalLink?: boolean;
  linkRouteName?: string;
  linkRouteParams?: GenericObject;
  linkQuery?: GenericObject;
  skipFocusClear?: boolean;
  isLoading?: boolean;
  lazyTooltip?: boolean;
}

const props = withDefaults(defineProps<FormButtonProps>(), {
  label: '',
  icon: '',
  iconProps: null,
  classes: '',
  tooltip: '',
  iconPosition: 'left',
  disabled: false,
  variant: 'primary',
  dataQa: '',
  link: '',
  linkRouteName: '',
  linkRouteParams: null,
  linkQuery: null,
  lazyTooltip: false,
});

interface FormButtonEmits {
  (e: 'button-click', value: Event): void;

  (e: 'focus', value: Event): void;

  (e: 'blur', value: Event): void;
}

const emit = defineEmits<FormButtonEmits>();

const isDisabled = computed(() => {
  const ignoreUpdateInProcess = props.ignoreUpdateInProcess || props.link || props.linkRouteName;
  return props.disabled || (props.isUpdateInProcess && !ignoreUpdateInProcess);
});

const linkObject = computed(() => {
  const returnObject: GenericObject = {
    name: props.linkRouteName,
    query: {},
  };
  if (props.linkRouteParams) {
    returnObject.params = props.linkRouteParams;
  }
  if (props.linkQuery) {
    returnObject.query = props.linkQuery;
  }
  return returnObject;
});

const buttonId = `btn-${Math.random().toString(36).substring(2, 10)}`;
const isTooltipActive = ref(false);
const hoverTimeout = ref(null);
const hideTimeout = ref(null);
const hoverDelay = 300;

const handleTooltipMouseEnter = (event: Event) => {
  if (hoverTimeout.value) {
    clearTimeout(hoverTimeout.value);
  }
  if (hideTimeout.value) {
    clearTimeout(hideTimeout.value);
    hideTimeout.value = null;
  }
  hoverTimeout.value = setTimeout(() => {
    isTooltipActive.value = true;
  }, hoverDelay);
  if (event) event.stopPropagation();
};

const handleTooltipMouseLeave = (event: Event) => {
  if (hoverTimeout.value) {
    clearTimeout(hoverTimeout.value);
    hoverTimeout.value = null;
  }
  hideTimeout.value = setTimeout(() => {
    if (!document.getElementById(buttonId)?.matches(':hover')) {
      isTooltipActive.value = false;
    }
  }, 50);
  if (event) event.stopPropagation();
};
onUnmounted(() => {
  if (hoverTimeout.value) {
    clearTimeout(hoverTimeout.value);
  }
  if (hideTimeout.value) {
    clearTimeout(hideTimeout.value);
  }
  isTooltipActive.value = false;
});
</script>
